import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Divider,
    Paper,
    Stack,
    Typography,
    Unstable_Grid2 as Grid,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import PageHeader from '../../components/page-header';
import {mockBusiness} from '../../mock-data';

const LoyaltySettingsView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.settings'});
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const {configureNotebookTags} = useConfigureNotebook('biz-point-loyalty-settings');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    /**
     * Configures notebook tags and behavioral analytics for the settings page.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointLoyaltySettings);
        configureBehavioralTool((tags) => tags.bizPointLoyaltySettings);
    }, [configureBehavioralTool, configureNotebookTags]);

    const schema = useMemo(() => Yup.object().shape({
        freezeDays: Yup.number()
            .typeError(t('freezeDaysRequired'))
            .required(t('freezeDaysRequired'))
            .integer(t('freezeDaysRequired'))
            .min(1, t('freezeDaysMin')),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        freezeDays: mockBusiness.freezeDays,
    }), []);

    /**
     * Saves freeze period via mock API when user has settings access.
     */
    const handleSaveFreeze = useCallback(async (values) => {
        if (!packages.bizPoint.loyaltySettings) {
            return;
        }
        setSaving(true);
        setSaved(false);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true});
        if (!isMounted()) {
            return;
        }
        setSaving(false);
        if (response?.resultFlag) {
            setSaved(true);
        }
    }, [packages.bizPoint.loyaltySettings, isMounted]);

    const commercialTerms = useMemo(() => ([
        {label: t('purchaseRate'), value: t('purchaseRateValue', {rate: mockBusiness.rateBizPer100})},
        {label: t('redemptionValue'), value: t('redemptionValueAmount', {value: mockBusiness.redeemPer900})},
        {label: t('tier'), value: mockBusiness.tier},
        {label: t('spread'), value: t('spreadValue', {spread: mockBusiness.spread})},
        {label: t('currency'), value: mockBusiness.currency},
    ]), [t]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Grid container spacing={3}>
                <Grid xs={12} md={7}>
                    <Paper variant="outlined" sx={{p: 3}}>
                        <Stack spacing={1} sx={{mb: 2.5}}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('freezePeriod')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('freezePeriodHelp')}
                            </Typography>
                        </Stack>

                        {saved && (
                            <Alert severity="success" sx={{mb: 2}} onClose={() => setSaved(false)}>
                                {t('saveSuccess')}
                            </Alert>
                        )}

                        {packages.bizPoint.loyaltySettings ? (
                            <Form
                                initialValues={initialValues}
                                validate={validate}
                                onSubmit={handleSaveFreeze}
                                formProps={{className: 'w-100'}}
                            >
                                <Stack spacing={2.5} sx={{maxWidth: 360}}>
                                    <MuiRFF.TextField
                                        name="freezeDays"
                                        label={t('freezeDaysLabel')}
                                        type="number"
                                        fullWidth
                                        required={required.freezeDays}
                                        inputProps={{min: 1}}
                                    />
                                    <Box>
                                        <Button type="submit" variant="contained" disabled={saving}>
                                            {t('save')}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Form>
                        ) : (
                            <Alert severity="info" variant="outlined" sx={{borderColor: 'info.main'}}>
                                {t('freezeReadOnly', {days: mockBusiness.freezeDays})}
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid xs={12} md={5}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            height: '100%',
                            bgcolor: 'action.hover',
                        }}
                    >
                        <Stack spacing={1} sx={{mb: 2}}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('commercialTerms')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('commercialTermsReadOnly')}
                            </Typography>
                        </Stack>

                        <Divider sx={{mb: 2}}/>

                        <Stack spacing={1.75} divider={<Divider flexItem/>}>
                            {commercialTerms.map((term) => (
                                <Stack
                                    key={term.label}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="baseline"
                                    spacing={2}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {term.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {term.value}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LoyaltySettingsView;
